import type { ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { Alert, Button, Descriptions, Skeleton, Tag } from 'antd'
import { ArrowLeft, FileText } from 'lucide-react'
import type { ServiceOrder } from '@super-mega/server/contracts/order'
import { AttachmentUpload } from '@/features/files/index.ts'
import { useAccess } from '@/hooks/use-access.ts'
import { useTRPC } from '@/trpc/react.ts'
import { formatAmount } from '@/utils/money.ts'
import { ORDER_STATUS } from './const/index-list.ts'
import { OrderArchiveNotice } from './order-archive-notice.tsx'
import { ServiceInitialBillPanel } from './service-initial-bill-panel.tsx'
import { ServiceLifecycleActions } from './service-lifecycle-actions.tsx'
import { ServiceTermPanel } from './service-term-panel.tsx'

export function ServiceOrderDetails({ id }: { id: number }) {
  const trpc = useTRPC()
  const query = useQuery(trpc.order.service.detail.queryOptions({ serviceOrderId: id }))
  if (query.isError) {
    return (
      <Alert
        type="error"
        title={query.error.message}
        action={<Button onClick={() => void query.refetch()}>重试</Button>}
      />
    )
  }
  if (!query.data) return <Skeleton active paragraph={{ rows: 8 }} />
  const order = query.data
  const render = (fees: ReactNode, actions: ReactNode) => (
    <ServiceOrderDetailLayout order={order} fees={fees} actions={actions} />
  )
  if (order.serviceMode === 'term') return <ServiceTermPanel order={order}>{render}</ServiceTermPanel>
  return <ServiceInitialBillPanel order={order}>{render}</ServiceInitialBillPanel>
}

function ServiceOrderDetailLayout({
  order,
  fees,
  actions
}: {
  order: ServiceOrder
  fees: ReactNode
  actions: ReactNode
}) {
  const access = useAccess()
  return (
    <>
      <section className="border-border bg-surface overflow-hidden rounded-xl border shadow-xs">
        <header className="border-border bg-surface-muted flex flex-wrap items-start justify-between gap-4 border-b p-5 sm:p-6">
          <div className="min-w-0 space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <Tag color={ORDER_STATUS[order.status].color}>{ORDER_STATUS[order.status].text}</Tag>
              <span className="text-content-muted text-sm">
                {order.serviceMode === 'term' ? '期限服务' : '一次性服务'}
              </span>
              {order.archivedAt && <Tag>已归档</Tag>}
            </div>
            <h2 className="text-content text-lg font-semibold break-words">{order.businessName}</h2>
            <p className="text-content-muted text-sm break-all">
              {order.orderNo} · {order.customerName}
            </p>
          </div>
          <div className="border-border bg-surface rounded-lg border px-4 py-2 text-center">
            <p className="text-content-muted text-xs">订单 ID</p>
            <p className="text-content mt-1 font-semibold">#{order.id}</p>
          </div>
        </header>
        <div className="p-5 sm:p-6">
          <SectionHeading title="订单归属与服务约定" />
          <Descriptions
            column={{ xs: 1, sm: 2, lg: 3 }}
            items={[
              { key: 'customer', label: '客户', children: order.customerName },
              { key: 'organization', label: '经营主体', children: order.organizationName },
              { key: 'business', label: '业务类型', children: order.businessName },
              { key: 'salesperson', label: '业务员', children: order.salespersonName || '—' },
              { key: 'subject', label: '业务主体', children: order.subjectName || '—' },
              {
                key: 'amount',
                label: '约定服务费',
                children: order.agreedAmount === null ? '按费用规则计费' : `¥${formatAmount(order.agreedAmount)}`
              }
            ]}
          />
        </div>
      </section>
      <OrderArchiveNotice archivedAt={order.archivedAt} />
      <section className="border-border bg-surface mt-4 rounded-xl border p-5 shadow-xs sm:p-6">
        <SectionHeading title={order.serviceMode === 'term' ? '服务期限与费用' : '服务费用'} />
        {fees}
      </section>
      <section className="border-border bg-surface mt-4 rounded-xl border p-5 shadow-xs sm:p-6">
        <SectionHeading title="附件与备注" />
        {order.attachments.length > 0 ? (
          <AttachmentUpload
            purpose="serviceAttachment"
            value={order.attachments.map(({ file }) => file.id)}
            disabled
            initialFiles={order.attachments.map(({ file }) => ({
              id: file.id,
              name: file.originName || file.fileName,
              fileType: file.fileType,
              size: Number(file.fileSize)
            }))}
          />
        ) : (
          <p className="text-content-muted mb-4 text-sm">暂无附件</p>
        )}
        <Descriptions
          column={1}
          items={[
            {
              key: 'note',
              label: '备注',
              children: <span className="break-words whitespace-pre-wrap">{order.note || '暂无备注'}</span>
            }
          ]}
        />
      </section>
      <footer
        aria-label="服务订单操作"
        className="border-border bg-surface/95 bottom-3 z-20 mt-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border px-4 py-3 shadow-md backdrop-blur sm:sticky"
      >
        <Link to="/orders/service" search={{}}>
          <Button icon={<ArrowLeft aria-hidden size={16} />}>返回服务列表</Button>
        </Link>
        <div className="flex flex-wrap items-center gap-2">
          <ServiceLifecycleActions order={order} />
          {actions}
          {access.visit(`/orders/service/contract/${order.id}`) && (
            <Button
              href={`/orders/service/contract/${order.id}`}
              target="_blank"
              rel="noopener noreferrer"
              icon={<FileText aria-hidden size={16} />}
            >
              查看合同 / 打印
            </Button>
          )}
        </div>
      </footer>
    </>
  )
}

function SectionHeading({ title }: { title: string }) {
  return (
    <h3 className="text-content mb-4 flex items-center gap-2 text-base font-semibold">
      <span aria-hidden className="bg-accent size-2 rounded-full shadow-[0_0_0_0.25rem_var(--accent-soft)]" />
      {title}
    </h3>
  )
}
